import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { assert } from '@ember/debug'
import { getOwner, setOwner } from '@ember/owner'
import { get as lodashGet } from 'lodash'
import { trackedFunction, type State } from 'reactiveweb/function'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import {
  ContentLoader,
  type ActivityManifestBundle,
  type CaperActivityManifest,
} from '@blakeelearning/content-loader-core'
import type { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type FeaturesService from 'teacher-ui-client/services/features'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import { Product } from 'teacher-ui-client/utils/products'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

// https://3plearning.atlassian.net/wiki/spaces/TY/pages/3811278862/Interactive+Preview+Engines+in+TUI

/**
 * trackedFunctions should be defined on classes with owners
 */
function setOwnerFromContext(previewEngine: PreviewEngine, context: object) {
  const owner = getOwner(context)
  assert('missing owner for preview engine', owner)
  setOwner(previewEngine, owner)
}

/**
 * manifestKey can be a string like 'readingeggs-1-end-of-lesson-skills-quiz', or an array like ['activities', '0']
 *
 * We turn the array into dot-notation, like activities.0
 */
function constructManifestPath({ manifestBundle, manifestKey }: SequenceItem): ManifestPath {
  const path = manifestBundle
  const hash = Array.isArray(manifestKey) ? manifestKey.join('.') : manifestKey
  return new ManifestPath(path, hash)
}

function isCaperActivityManifest(manifestBundle: unknown): manifestBundle is CaperActivityManifest {
  return (
    typeof manifestBundle === 'object' &&
    manifestBundle !== null &&
    'content' in manifestBundle &&
    typeof manifestBundle.content === 'object' &&
    manifestBundle.content !== null
  )
}

function getVariableFromCaperManifest(manifestBundle: ActivityManifestBundle, variable: string): unknown {
  if (isCaperActivityManifest(manifestBundle) && manifestBundle.content.variables) {
    return manifestBundle.content.variables[variable]
  }
}

/**
 * Generates a url for a caper interactive manifest from the tomfoolery standalone deploy
 *
 * eg https://example.com/readingeggs/releases/staging/data/au/lessons/1.json#activities.0
 */
function caperTomfooleryUrlForInteractive(activityNamespace: string, curriculum: string, manifestPath: ManifestPath) {
  const { assetHosts, releaseSha } = config.contentLoader.caperTomfoolery
  const releaseNamespace = `/releases/${releaseSha}` // staging, qa, production

  const url = join(assetHosts.code, activityNamespace, releaseNamespace, 'data', curriculum, manifestPath.path)
  url.pathname += '.json'
  if (manifestPath.hash) url.hash = manifestPath.hash
  return url.href
}
function createCaperLoader(variant: string) {
  return new ContentLoader({
    framework: 'caper',
    variant,
    config: { ...config.contentLoader.caperTomfoolery },
  })
}

function createJesterLoader(variant: string, namespace: string) {
  return new ContentLoader({
    framework: 'jester',
    variant,
    config: {
      ...config.contentLoader.jester,
      activities: {
        namespace, // jesters' activity builder uses these from the config to build a url
        releaseSha: config.contentLoader.jester.activities.releaseSha,
      },
    },
  })
}

function parseVariant(variant: string, options: { isCanadaAware: boolean }) {
  const { isCanadaAware } = options
  const isCaVariant = variant === 'ca'

  /**
   * RE/REX caper activities (incl books) should be redirected from CA -> US (should provide isCanadaAware: false)
   * MS caper activities can keep CA variant (should provide isCanadaAware: true)
   * Jester activities should be redirected from CA -> US (should provide isCanadaAware: false)
   * https://3plearning.slack.com/archives/C01QGSBD52R/p1714521134159539
   * https://github.com/blake-education/fast-phonics-client/blob/e06e18742ac4d7365b8ede9a2e6dce2d8102d6df/app/services/session.ts#L103-L112
   */
  return !isCanadaAware && isCaVariant ? 'us' : variant
}

interface BookData extends Record<string, unknown> {
  name: string
  code: string
}

/**
 * Use as handler for a proxy in order to return defined items, or a noop function otherwise.
 * This is useful for externalController, so we don't need to implement every function like "saveProgress" or
 * "incrementScore".
 *
 * Also serves as a good inspection point for finding out what items the interactive uses from the externalController
 */
const StubFunctionHandler: ProxyHandler<Record<PropertyKey, unknown>> = {
  get: function (target, prop, receiver) {
    if (Reflect.ownKeys(target).includes(prop)) {
      return Reflect.get(target, prop, receiver)
    } else if (prop === 'readyForUserInput') {
      // jester's way of signalling it's done loading
      // make it do the same as caper (send a LOAD_COMPLETE event) to make handling easier
      return () => document.dispatchEvent(new Event('LOAD_COMPLETE'))
    } else {
      return () => {}
    }
  },
}

const validToolkitActivityNames = [
  'buzzys_word_machine',
  'making_words',
  'sorting_words',
  'letter_grid',
  'dot_to_dot',
  'sound_match',
  'label_it',
] as const
type ToolkitActivityName = (typeof validToolkitActivityNames)[number]

export function isValidToolkitActivity(maybeActivityName: unknown): maybeActivityName is ToolkitActivityName {
  return (
    typeof maybeActivityName === 'string' &&
    (validToolkitActivityNames as readonly string[]).includes(maybeActivityName)
  )
}

/**
 * Defines the interface to fetch a manifest. Resembles URL properties.
 */
class ManifestPath {
  path: string
  hash?: string
  constructor(path: string, hash?: string) {
    this.path = path
    this.hash = hash
  }
  toString() {
    return `${this.path}#${this.hash ? this.hash : ''}` // eg lessons/1#activities.0
  }
}

/**
 * A single item from a progression sequence, which should contain data about an individual activity, including how to
 * load it.
 */
export interface SequenceItem extends Record<string, unknown> {
  /**
   * This indicates part of the path where the manifest is located for this activity
   */
  manifestBundle: string
  /**
   * Indicates where in the bundle to look for the manifest for this activity.
   * string[] keys are typically used by caper manifests.
   */
  manifestKey?: string | string[]
}

interface NavigationItem {
  id: string
  label: string
  current: boolean
  onClick: () => unknown
}

type AnalyticsValue = string | number | boolean | undefined

export interface InteractiveAnalyticsMetadata {
  product: Product
  eventData: {
    interactiveType: string
    activityNamespace: string
    curriculum: string
    [key: string]: AnalyticsValue
  }
}

function buildAnalyticsMetadata(
  interactive: Pick<PreviewEngine, 'activityNamespace' | 'curriculum'>,
  product: Product,
  interactiveType: string,
  eventData: Record<string, AnalyticsValue> = {},
): InteractiveAnalyticsMetadata {
  return {
    product,
    eventData: {
      interactiveType,
      activityNamespace: interactive.activityNamespace,
      curriculum: interactive.curriculum,
      ...eventData,
    },
  }
}

export interface PreviewEngine {
  activityNamespace: string

  curriculum: string

  /**
   * Retrieves the relevant sequence items from an external source, for the scope of the engine. These may be used to
   * navigated through, each item providing data about how to load its interactive.
   *
   * Each item typically represents an activity within a lesson, but is not necessarily restricted as such.
   */
  sequenceItems: State<Promise<SequenceItem[]> | SequenceItem[]>

  /**
   * Indicates which item in the sequence to present
   */
  sequenceIndex: number

  /**
   * Gets the sequenceItem from the array of items using the index.
   */
  readonly sequenceItem: SequenceItem | undefined

  /**
   * Indicates if the sequenceItem uses the caper framework
   */
  readonly isCaper?: boolean

  /**
   * May be a caper or jester loader
   */
  loader: ContentLoader

  /**
   * A tracked function that will use the loader to fetch the manifest bundle from the manifest bundle path. If needed,
   * can return multiple bundles.
   */
  manifestBundle: State<Promise<ActivityManifestBundle[]> | ActivityManifestBundle[]>

  /**
   * The manifest for the interactive to be run. Handed to ContentLoader in the component.
   */
  readonly currentManifest: ActivityManifestBundle[]

  /**
   * Optionally provide variables to be given to the interactive.
   */
  variables?: Record<string, unknown>

  /**
   * Provide various action stubs to the interactives
   */
  readonly externalController: Record<string, unknown> | undefined

  /**
   * Optionally override to provide navigation items.
   * Rendered as buttons in a nav bar, intended to help navigate between interactives in a manifest bundle
   */
  readonly navigation?: NavigationItem[]

  /**
   * Optionally override to provide a way to choose a different part of the lesson preview.
   * Index is a string, as this is called from a html select element.
   */
  readonly goToPart?: (index: string) => void
  /**
   * Optionally override to provide a way to the next part of the lesson preview
   */
  readonly goToNext?: () => void

  /**
   * Returns the subscription type and metadata used to track interactive opens.
   */
  getAnalyticsMetadata: () => InteractiveAnalyticsMetadata

  /**
   * Called when the engine has reached the end of its configued set of activities. Typically used to close the window.
   */
  onComplete?: () => void
}

/**
 * To help with acceptance tests, to render a mocked preview.
 *
 * Potentially, this could point at a test activity if one were to exist (if the manifest bundle was stored locally)
 */
export class TestPreview implements PreviewEngine {
  constructor(context: object) {
    setOwnerFromContext(this, context)
  }
  activityNamespace = 'test-namespace'
  curriculum = 'test-curriculum'
  loader = createCaperLoader('au')
  sequenceItems = trackedFunction(this, async () => {
    // this endpoint is intended to be mocked to return the result you want for the test
    const response = await fetch('/preview-engine-test/sequence-items', { method: 'GET' })
    if (response.ok) {
      const result = (await response.json()) as SequenceItem[]
      return result
    }
    return []
  })
  sequenceIndex = 0
  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }
  manifestBundle = trackedFunction(this, async () => {
    // this endpoint is intended to be mocked to return the result you want for the test
    const response = await fetch('/preview-engine-test/manifest-bundle', { method: 'GET' })
    if (response.ok) {
      const result = (await response.json()) as ActivityManifestBundle[]
      return result
    }
    return []
  })
  get currentManifest() {
    return this.manifestBundle.value ?? []
  }
  externalController = undefined

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.RE, 'test-preview')
  }
}

export class ToolkitPreview implements PreviewEngine {
  activityNamespace = 'teacherui'

  curriculum: string

  name: ToolkitActivityName

  id: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, name: ToolkitActivityName, id: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.name = name
    this.id = id

    this.loader = createCaperLoader(this.curriculum)
  }

  sequenceItems = trackedFunction(this, () => {
    // fake array of sequence items
    return [{ manifestBundle: `teacherui/activities/${this.name}/${this.id}` }]
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)
    const manifestBundle = await this.loader.fetchManifest(caperUrl)
    return [manifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    return new Proxy(
      {
        saveProgress: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.RE, 'toolkit-preview', {
      activityName: this.name,
      activityId: this.id,
    })
  }

  onComplete() {}
}

export class ReadingEggsLessonPreview implements PreviewEngine {
  activityNamespace = 'readingeggs'

  curriculum: string

  precinct: typeof Precinct.RE_READING | typeof Precinct.RE_SPELLING | typeof Precinct.RE_STORYLANDS

  lesson: string

  caperLoader: ContentLoader

  jesterLoader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, precinct: ReadingEggsLessonPreview['precinct'], lesson: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.precinct = precinct
    this.lesson = lesson

    this.caperLoader = createCaperLoader(this.curriculum)
    this.jesterLoader = createJesterLoader(this.curriculum, this.activityNamespace)
  }

  // Convert our own precinct into one used by external systems
  get remotePrecinct() {
    switch (this.precinct) {
      case Precinct.RE_READING:
        return 'reading'
      case Precinct.RE_SPELLING:
        return 'spelling'
      case Precinct.RE_STORYLANDS:
        return 'clinker_castle'
    }
  }

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-readingeggs/dist/v2/progression.json')

    const hasBundle = (item: Record<string, unknown>): item is SequenceItem => 'manifestBundle' in item

    const items = progressionSequence.reduce<SequenceItem[]>((items, item) => {
      if (
        hasBundle(item) &&
        item.precinct === this.remotePrecinct &&
        item.lessonInPrecinct === parseInt(this.lesson, 10)
      ) {
        return [...items, item]
      }
      return items
    }, [])

    return items
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  get isCaper() {
    return this.sequenceItem?.framework === 'caper'
  }

  get loader() {
    return this.isCaper ? this.caperLoader : this.jesterLoader
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    if (this.isCaper) {
      const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)
      const manifestBundle = await this.loader.fetchManifest(caperUrl)
      return [manifestBundle]
    }
    return [await this.loader.fetchManifest(manifestPath.toString())]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    const saveResult = () => {
      // make sure they've earned an egg, so that the "next" button is displayed
      return { ok: true, data: { eggsEarned: 1, passed: true } }
    }
    return new Proxy(
      {
        // some storylands lessons call this and expect a promise (eg lesson 1 part 7) with quiz results
        // https://github.com/blake-education/caper-activities/blob/160ada4ec24747bae0aac97824d36b4751d525e4/lib/assets/javascripts/readingeggs/activities/clinker_castle/quiz/main_game_state.coffee#L20
        reportQuizState: () =>
          Promise.resolve({
            currentQuestion: 1,
            correctAnswers: [],
            incorrectAnswers: [],
            score: 0,
          }),
        saveProgress: () => {
          this.goToNext()
          return { ok: true }
        },
        // https://github.com/blake-education/re-client/blob/60ee3a84e4e40b6800514ae42dc4807f1e61d70c/app/controllers/lessons/lesson/activity.ts#L411-L436
        goNext: () => {
          this.goToNext()
        },
        saveSkillsQuizResult: saveResult,
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    return (this.sequenceItems.value ?? []).map((_item, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }

  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) {
      this.sequenceIndex += 1
    } else {
      this.onComplete()
    }
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.RE, 'reading-eggs-lesson', {
      precinct: this.precinct,
      lesson: this.lesson,
    })
  }

  onComplete() {}
}

export class ReadingEggsMapQuizPreview implements PreviewEngine {
  activityNamespace = 'readingeggs'

  curriculum: string

  precinct: typeof Precinct.RE_READING | typeof Precinct.RE_SPELLING

  map: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, precinct: ReadingEggsMapQuizPreview['precinct'], map: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.precinct = precinct
    this.map = map

    this.loader = createCaperLoader(this.curriculum)
  }

  sequenceItems = trackedFunction(this, () => {
    // fake array of sequence items
    switch (this.precinct) {
      case Precinct.RE_READING:
        return [{ manifestBundle: `reading/quizzes/${this.map}` }]
      case Precinct.RE_SPELLING:
        return [{ manifestBundle: `spelling/quizzes/${this.map}` }]
    }
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)
    const manifestBundle = await this.loader.fetchManifest(caperUrl)
    return [manifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    return new Proxy(
      {
        quizCompleted: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.RE, 'reading-eggs-map-quiz', {
      precinct: this.precinct,
      map: this.map,
    })
  }

  onComplete() {}
}

export class ReadingEggspressReadingLessonPreview implements PreviewEngine {
  curriculum: string

  lesson: string

  caperLoader: ContentLoader
  jesterLoader: ContentLoader

  get isCaper() {
    return this.sequenceItem?.framework === 'caper'
  }

  get loader() {
    return this.isCaper ? this.caperLoader : this.jesterLoader
  }

  get activityNamespace() {
    return this.isCaper ? 'rex_student' : 'rex-student'
  }

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, lesson: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.lesson = lesson

    this.caperLoader = createCaperLoader(this.curriculum)
    this.jesterLoader = createJesterLoader(this.curriculum, this.activityNamespace)
  }

  remotePrecinct = 'my_lessons'

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-rex-student/dist/preview/progression.json')

    const hasBundle = (item: Record<string, unknown>): item is SequenceItem => 'manifestBundle' in item

    const items = progressionSequence.reduce<SequenceItem[]>((items, item) => {
      if (
        hasBundle(item) &&
        item.precinct === this.remotePrecinct &&
        item.lessonInPrecinct === parseInt(this.lesson, 10)
      ) {
        return [...items, item]
      }
      return items
    }, [])

    return items
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    if (this.isCaper) {
      const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)

      const manifestBundle = await this.loader.fetchManifest(caperUrl)
      return [manifestBundle]
    }
    return [await this.loader.fetchManifest(manifestPath.toString())]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    return new Proxy(
      {
        saveProgress: () => {
          this.goToNext()
        },
        // Seems that each rex lesson ends with a book quiz
        saveChapterQuizScore: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    return (this.sequenceItems.value ?? []).map((_item, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }
  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) {
      this.sequenceIndex += 1
    } else {
      this.onComplete()
    }
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.REX, 'reading-eggspress-reading-lesson', {
      lesson: this.lesson,
      precinct: this.remotePrecinct,
    })
  }

  onComplete() {}
}

export class ReadingEggspressSpellingLessonPreview implements PreviewEngine {
  activityNamespace = 'rex_student'

  curriculum: string

  lesson: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, lesson: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.lesson = lesson

    this.loader = createCaperLoader(this.curriculum)
  }

  remotePrecinct = 'english_skills_spelling'

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-rex-student/dist/progression.json')
    return progressionSequence.filter(
      (item) => item.precinct === this.remotePrecinct && item.lessonInPrecinct === parseInt(this.lesson, 10),
    )
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  // rex spelling is a little special, requiring 2 manifests to load its interactive
  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const stadiumUrl = caperTomfooleryUrlForInteractive(
      this.activityNamespace,
      this.curriculum,
      new ManifestPath('english_skills_spelling/activities/stadium_spelling'),
    )
    const lessonUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)

    const [stadiumManifestBundle, lessonManifestBundle] = await Promise.all([
      this.loader.fetchManifest(stadiumUrl),
      this.loader.fetchManifest(lessonUrl),
    ])
    return [stadiumManifestBundle, lessonManifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  @tracked variables = {}

  get externalController() {
    return new Proxy(
      {
        /**
         * Mid-lesson, rex spelling would take the student to a game, then back to the lesson. It's the same interactive
         * but it uses the variables to tell the interactive the students' progress through it.
         * Note that by setting the variables & consuming immediately, we're skipping the game.
         */
        showGame: (_gameId: unknown, spellingState: Record<string, unknown>) => {
          this.variables = spellingState
        },
        completeLesson: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.REX, 'reading-eggspress-spelling-lesson', {
      lesson: this.lesson,
      precinct: this.remotePrecinct,
    })
  }

  onComplete() {}
}

export class BookPreview implements PreviewEngine {
  @service authToken!: AuthToken

  activityNamespace = 'rex_student'

  curriculum: string

  bookCode: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, bookCode: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.bookCode = bookCode

    this.loader = createCaperLoader(this.curriculum)
  }

  remotePrecinct = 'my_lessons'

  sequenceItems = trackedFunction(this, () => {
    // fake array of sequence items
    return [{ manifestBundle: `${this.remotePrecinct}/activities/library_reading` }]
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)
    const [manifestBundle, bookResponse] = await Promise.all([
      this.loader.fetchManifest(caperUrl),
      fetch(`${config.libraryBookDataUrl}${this.bookCode}`, {
        headers: {
          Authorization: this.authToken.token ?? '',
          accent: this.curriculum,
          Accept: 'application/json',
        },
      }),
    ])

    if (!bookResponse.ok) return []
    const book = (await bookResponse.json()) as BookData

    // student app camelizes various book keys
    // https://github.com/blake-education/rex-client/blob/fdfaa9c4bff5d015d44d360adb59edd436aa57a2/app/serializers/book.js#L4-L10
    // keep it explicit and just do the ones we need

    // determines whether to display "Read to me" speakers. Only want them displayed if narration audio is present, rather than any audio (eg quiz question audio)
    book.audioAvailable = book.narration_audio_available

    // TODO fetch quiz progress from Library API, then allow book quizzes to be shown
    // book.quizAvailable = book.quiz_available
    // book.chapterQuizzesAvailable = book.chapter_quizzes_available

    // library activity needs the book data in variables
    this.variables = { book, name: book.name, bookCode: book.code }
    return [manifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  @tracked variables = {}

  get externalController() {
    return new Proxy(
      {
        closeBook: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.REX, 'book-preview', {
      bookCode: this.bookCode,
      precinct: this.remotePrecinct,
    })
  }

  onComplete() {}
}

export class BigBookPreview extends BookPreview {
  subscriptionType: SubscriptionType

  bigBookLevel?: string

  constructor(
    context: object,
    curriculum: string,
    bookCode: string,
    subscriptionType: SubscriptionType,
    bigBookLevel?: string,
  ) {
    super(context, curriculum, bookCode)
    this.subscriptionType = subscriptionType
    this.bigBookLevel = bigBookLevel
  }

  getAnalyticsMetadata = () => {
    let product: Product

    switch (this.subscriptionType) {
      case SubscriptionType.Reading:
        product = Product.RE
        break
      case SubscriptionType.Maths:
        product = Product.MS
        break
      default:
        product = Product.RE
    }

    return buildAnalyticsMetadata(this, product, 'big-book-open', {
      bookCode: this.bookCode,
      bigBookLevel: this.bigBookLevel,
      precinct: this.remotePrecinct,
    })
  }
}

export class FastPhonicsLessonPreview implements PreviewEngine {
  activityNamespace = 'fast-phonics'

  curriculum: string

  lesson: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, lesson: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.lesson = lesson

    this.loader = createJesterLoader(this.curriculum, this.activityNamespace)
  }

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-fast-phonics/dist/progression.json')
    return Object.values(progressionSequence).filter(
      (item) => item.peak === parseInt(this.lesson, 10) && item.type !== 'unlock',
    )
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const manifestBundle = await this.loader.fetchManifest(manifestPath.toString())
    return [manifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    return new Proxy(
      {
        completeVideo: () => {
          this.goToNext()
        },
        goNext: () => {
          this.goToNext()
        },
        savePeakQuizResult: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    return (this.sequenceItems.value ?? []).map((_item, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }
  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) {
      this.sequenceIndex += 1
    } else {
      this.onComplete()
    }
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.FP, 'fast-phonics-lesson', {
      lesson: this.lesson,
    })
  }

  onComplete() {}
}

export class MathseedsLessonPreview implements PreviewEngine {
  activityNamespace = 'mathseeds'

  curriculum: string

  lesson: string

  loader: ContentLoader

  constructor(context: object, curriculum: string, lesson: string) {
    setOwnerFromContext(this, context)

    this.curriculum = parseVariant(curriculum, { isCanadaAware: true }) // MS caper is canada aware
    this.lesson = lesson

    this.loader = createCaperLoader(this.curriculum)
  }

  remotePrecinct = 'mathseeds'

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-mathseeds/dist/progression.json')
    return progressionSequence.filter(
      (item) => item.precinct === this.remotePrecinct && item.lessonInPrecinct === parseInt(this.lesson, 10),
    )
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const caperUrl = caperTomfooleryUrlForInteractive(this.activityNamespace, this.curriculum, manifestPath)
    const [lessonManifest] = await Promise.all([this.loader.fetchManifest(caperUrl)])

    // Some Mathseeds lessons have companion manifests that contain depedendencies. The lesson manifest will tell us
    if (getVariableFromCaperManifest(lessonManifest, 'include_caper_intro_outro')) {
      // If map > 30, teen.json, else junior.json
      // Lesson 150 is last lesson in map 30
      const introOutroType = Number.parseInt(this.lesson, 10) >= 151 ? 'teen' : 'junior'
      const introPath = caperTomfooleryUrlForInteractive(
        this.activityNamespace,
        this.curriculum,
        new ManifestPath(`mathseeds/lessons/activity_intros/${introOutroType}`),
      )
      const outroPath = caperTomfooleryUrlForInteractive(
        this.activityNamespace,
        this.curriculum,
        new ManifestPath(`mathseeds/lessons/activity_outros/${introOutroType}`),
      )

      const [introManifest, outroManifest] = await Promise.all([
        this.loader.fetchManifest(introPath),
        this.loader.fetchManifest(outroPath),
      ])

      /* For some reason we need to modify the structure to get it to work.
       * The student app randomises the intro/outro here, we'll just pick the first.
       * https://github.com/blake-education/mathseeds-client/blob/e15b6b2b7cc7247b4db27c1a39f79e616b99cac3/app/lesson/activity/route.js#L69-L84
       */
      const intro = Array.isArray(introManifest.intros) ? { content: introManifest.intros[0] } : {}
      const outro = Array.isArray(outroManifest.outros) ? { content: outroManifest.outros[0] } : {}

      return [lessonManifest, intro, outro]
    }
    return [lessonManifest]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    return new Proxy(
      {
        saveProgress: () => {
          this.goToNext()
        },
        next: () => {
          this.onComplete()
        },
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    return (this.sequenceItems.value ?? []).map((_item, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }
  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) {
      this.sequenceIndex += 1
    } else {
      this.onComplete()
    }
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.MS, 'mathseeds-lesson', {
      lesson: this.lesson,
      precinct: this.remotePrecinct,
    })
  }

  onComplete() {}
}

export class MathseedsPrimeLessonPreview implements PreviewEngine {
  activityNamespace = 'mathseeds-prime'

  curriculum: string

  threadId: MathseedsPrimeThread

  unitId: number

  lessonId: number

  loader: ContentLoader

  @service features!: FeaturesService

  /* TUI has curriculum (accent) and MSP has its own concept of curriculumId, intended to be a subset of accent, to
   * allow for different curriculums within a country.
   */
  constructor(context: object, curriculum: string, threadId: MathseedsPrimeThread, unitId: number, lessonId: number) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    // This is meant to be accent-curriculumId. But as of writing we do not have multiple curriculums for an accent.
    const mspVariant = `${this.curriculum}-${this.curriculum}`

    this.threadId = threadId
    this.unitId = unitId
    this.lessonId = lessonId

    this.loader = createJesterLoader(mspVariant, this.activityNamespace)
  }

  sequenceItems = trackedFunction(this, async () => {
    const { default: progressionSequence } =
      await import('@blakeelearning/progression-sequence-mathseeds-prime/dist/progression.json')
    const keyPrefix = `${this.curriculum}-${this.threadId}-${this.unitId}-${this.lessonId}-`
    const manifestBundle = `${this.threadId}/${this.unitId}`
    return Object.entries(progressionSequence).reduce<SequenceItem[]>((items, [manifestKey, item]) => {
      // Only include items that are within the relevant scope
      const isRelevant = manifestKey.startsWith(keyPrefix)
      // MSP sequence items dont seem to have a manifestBundle or manifestKey inside the item.
      return isRelevant ? [...items, { manifestBundle, manifestKey, ...item }] : items
    }, [])
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    const manifestBundle = await this.loader.fetchManifest(manifestPath.toString())
    return [manifestBundle]
  })

  get currentManifest() {
    return this.manifestBundle.value ?? []
  }

  get externalController() {
    const saveResult = () => {
      // make sure they've earned a coin, so that the "next" button is displayed
      return { ok: true, data: { coinsEarned: 1, passed: true } }
    }
    return new Proxy(
      {
        // sourced actions list from https://github.com/blake-education/mathseeds-prime-client/blob/1fc6f216521b6e2f2fc623ed9809a50bad14468e/src/parts/activity/actions.ts
        goNext: () => {
          this.goToNext()
        },
        saveActivityResult: saveResult,
        saveTeachingVideoResult: saveResult,
        saveReviewSkillsResult: saveResult,
        saveInteractiveActivityResult: saveResult,
        saveEndOfLessonQuizResult: saveResult,
        saveCheckupQuizResult: saveResult,
        saveProblemSolvingActivityResult: saveResult,
        saveEndOfUnitQuizResult: saveResult,
        saveEndOfGradeSkillsQuizResult: saveResult,
        saveEndOfGradeApplicationQuizResult: saveResult,
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    return (this.sequenceItems.value ?? []).map((_item, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }
  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) this.sequenceIndex += 1
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.MS, 'mathseeds-prime-lesson', {
      threadId: this.threadId,
      unitId: this.unitId,
      lessonId: this.lessonId,
    })
  }
}

export class WritingLegendsLessonPreview implements PreviewEngine {
  activityNamespace = 'writing-legends'

  curriculum: string

  lesson: string

  loader: ContentLoader

  @service features!: FeaturesService

  constructor(context: object, curriculum: string, lesson: string) {
    setOwnerFromContext(this, context)

    const isCanadaAware = this.features.isEnabled('canada_aware')
    this.curriculum = parseVariant(curriculum, { isCanadaAware })
    this.lesson = lesson

    this.loader = createJesterLoader(this.curriculum, this.activityNamespace)
  }

  sequenceItems = trackedFunction(this, () => {
    /*
     * The progression sequence for WL is incorrect & incomplete, so we fake it. However, we do not know how many
     * activities are in a lesson until the manifest bundle is downloaded. A bit of a chicken/egg problem.
     *
     * We deal with this by just generating more sequence items than will be needed, and ensuring the navigation only
     * allows navigating to activities that appear in the manifest bundle.
     */
    return Array.from({ length: 20 }, (_, index) => ({
      manifestBundle: `activities/lesson/${this.lesson}`,
      // 5 lessons per map
      manifestKey: `map-${Math.floor((parseInt(this.lesson, 10) - 1) / 5) + 1}-lesson-${this.lesson}-part-${index + 1}`,
    }))
  })

  @tracked sequenceIndex = 0

  get sequenceItem() {
    return this.sequenceItems.value?.[this.sequenceIndex]
  }

  manifestBundle = trackedFunction(this, async (): Promise<ActivityManifestBundle[]> => {
    if (!this.sequenceItem) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    /**
     * Because our sequence items are faked, we need to get our navigation information from the manifest instead. This
     * means we need to fetch the full lesson manifest, rather than just a single activity part of it. This is acheived
     * by fetching only with the path (no hash key).
     *
     * The currentManifest getter is then responsible for pulling out the activity manifest.
     */
    const manifestBundle = await this.loader.fetchManifest(manifestPath.path)
    return [manifestBundle]
  })

  get currentManifest() {
    if (!this.sequenceItem || !this.manifestBundle.value) return []
    const manifestPath = constructManifestPath(this.sequenceItem)

    if (!manifestPath.hash) return this.manifestBundle.value
    return [lodashGet(this.manifestBundle.value[0], manifestPath.hash) as ActivityManifestBundle]
  }

  get externalController() {
    return new Proxy(
      {
        goNext: () => {
          this.goToNext()
        },
      },
      StubFunctionHandler,
    )
  }

  get navigation() {
    // Because our sequence items are faked, we need to generate our navigation from the real manifest bundle instead
    const navItems = Object.keys(this.manifestBundle.value?.[0] ?? {})?.length ?? 0
    return Array.from({ length: navItems }, (_, index) => ({
      id: String(index),
      label: `${index + 1}`,
      current: this.sequenceIndex === index,
      onClick: () => (this.sequenceIndex = index),
    }))
  }

  goToPart = (index: string) => {
    this.sequenceIndex = parseInt(index, 10)
  }
  goToNext = () => {
    const sequenceLength = this.navigation.length ?? 0
    if (this.sequenceIndex + 1 < sequenceLength) {
      this.sequenceIndex += 1
    } else {
      this.onComplete()
    }
  }

  getAnalyticsMetadata = () => {
    return buildAnalyticsMetadata(this, Product.WL, 'writing-legends-lesson', {
      lesson: this.lesson,
    })
  }

  onComplete() {}
}
